/**
 * @React
 */
import React, { useEffect, useState } from "react";
/**
 * @DateFormater
 */
import dateFormat from "dateformat";
/**
 * @Mui
 */
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CardContent,
  IconButton,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
} from "@mui/material";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DownloadDoneOutlinedIcon from "@mui/icons-material/DownloadDoneOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
/**
 * @Context
 */
import { useGeneralInfoContext } from "context";
import { useAuth } from "context/AuthContext";
/**
 * @Components
 */
import Switch from "components/CustomComponents/SwitchComponent";
import Pagination from "components/common/Pagination";
import TableSkeleton from "components/TableSkeleton";
import { FilterGrid, FilterTextField, FilterSelect, FilterMenuItem, AssignmentMultiSelect } from "components/common/FilterControls";
import CustomTooltip from "components/CustomComponents/CustomTooltip";
import CustomInputFields from "components/CustomComponents/CustomInputFields";
import BulkActionsBar, { BulkAction } from "components/common/BulkActionsBar";
/**
 * @Types
 */
import { ApplicationPreview, Type, Filter, monitoringStatus, IMonitoringApprovalHistory, IMonitoringAgreementCgsModel } from "types";
import { evaluateEditRules } from "./editRules";
/**
 * @Utilities
 */
import openInNewTab from "utilities/newTab";
import { useFetchData } from "hooks";
import { generateUrl } from "utilities/generateRequestUrl";
import config from "utilities/generateRequestUrl/config";
import { ApiCgsListResponse, IMonitoringCgsFields } from "types/monitoringCgsFields";
import axiosInstance from "utilities/axios";
import { isMoreThanMonth, isOverdue, formatMonitoringDeadline, snapToGeorgiaEndOfDay } from "utilities/dates";
import DialogWindow from "components/CustomComponents/DialogWindow";
import { useSelectApplications } from "hooks/useSelectAll";
import { useAccess } from "hooks/useAccess";
import useMutation from "hooks/useMutation";

const STATUS_COLOR_MAP: Record<string, string> = {
  APPROVED:                    "#357a38", // green
  UNDER_REVIEW:                "#357a38", // green
  REJECTED:                    "#F57C00", // orange
  REMOVAL:                     "#F57C00", // orange
  REMONITORING:                "#1565c0", // blue
  REMONITORING_PENDING_AGENCY: "#c62828", // red
};

const getStatusColor = (tab: Type, application: any): { backgroundColor: string; color: boolean } => {
  const isExpired = isOverdue(application.monitoring_deadline);

  if (tab === Type.agreement) {
    if (!application.is_monitorable) return { backgroundColor: "inherit", color: false };
    return { backgroundColor: isExpired ? "#F57C00" : "#357a38", color: true };
  }

  const backgroundColor = STATUS_COLOR_MAP[application.monitoring_approval_status] ?? "inherit";
  return { backgroundColor, color: backgroundColor !== "inherit" };
};

const tabStatusMap: Record<string, (keyof typeof monitoringStatus)[]> = {
  monitoring: ["REMONITORING", "REMONITORING_PENDING_AGENCY", "UNDER_REVIEW", "REJECTED"], // show only some
  approved: ["APPROVED", "REMOVAL"], // show re-monitoring related
};

interface MonitoringDashboardProps {
  tab: Type;
}

type AssignmentOption = { _id: string; application_type: string; assigned_at: string; ended_at: string | null; is_active: boolean; organization: { _id: string; name: string } };

const MonitoringDashboard: React.FC<MonitoringDashboardProps> = ({ tab }) => {
  const { canSeeAllApps, isMonitoring, isCgs, isAgency, isAdmin, isHead, canReviewApp, canSubmitApp, canManageAgreements, canDeleteAgreements } = useAccess();
  const { user: currentUser, loading: authLoading } = useAuth();
  const [assignments, setAssignments] = React.useState<AssignmentOption[]>([]);
  const [filterReady, setFilterReady] = React.useState(false);

  React.useEffect(() => {
    if (authLoading) return;
    if (canSeeAllApps) {
      axiosInstance.get(generateUrl(config.client, "getAssignments", null, { application_type: "cgs" }))
        .then((res) => {
          setAssignments(res.data);
          const activeIds = (res.data as AssignmentOption[])
            .filter((a) => a.is_active)
            .map((a) => a._id);
          if (activeIds.length > 0) {
            setFilterState({ monitoring_assignment: activeIds } as any);
          }
          setFilterReady(true);
        })
        .catch(() => setFilterReady(true));
    } else {
      setFilterReady(true);
    }
  }, [authLoading, canSeeAllApps]);

  const [filter, setFilter] = useState<Filter>({
    company_name: "",
    company_identification_code: "",
    page: 1,
    amountOnPage: 15,
    type: tab,
    municipality: "",
    component: "",
    ...(Type.agreement === tab && { is_monitorable: true }),
    ...(Type.agreement !== tab && { monitoring_approval_status: "" }),
    ...(Type.monitoring === tab && { is_deadline_approve: true }),
    ...(Type.approved === tab && { is_paid: false }),
  });

  const setFilterState = (newFilter: Partial<Filter>) => {
    setFilter((prev) => {
      const shouldResetPage = Object.keys(newFilter).some((key) => key !== "page" && newFilter[key as keyof Filter] !== prev[key as keyof Filter]);
      return {
        ...prev,
        ...newFilter,
        page: shouldResetPage ? 1 : newFilter.page ?? prev.page,
      };
    });
  };

  type Column = {
    id: keyof ApplicationPreview | "bulk_select";
    label: string;
  };

  const url = filterReady ? generateUrl(config.client, "getAgreementCgs", null, filter as any) : "";
  const { data, load, error, fetchData } = useFetchData<ApiCgsListResponse | null>(url);

  // ── TAB 3 (approved) bulk payment selection — DO NOT TOUCH ──────────────────
  const {
    selectedIds,
    allSelected,
    someSelected,
    singleSelected,
    isModalOpen,
    setIsModalOpen,
    toggleSelectAll,
    toggleItem,
    isBulkAction,
    setIsBulkAction,
  } = useSelectApplications(
    (data?.results && data?.results.length > 0
      ? data.results
        .filter((item) => !!item._id)
        .map((item) => {
          return ({
            _id: item._id as string,
            is_paid: Boolean((item as any).is_paid),
          })
        })
      : []) as { _id: string; is_paid: boolean }[]
  );

  // ── TAB 1 (agreement) bulk selection state ──────────────────────────────────
  const [tab1SelectedIds, setTab1SelectedIds] = useState<string[]>([]);

  const clearTab1Selection = () => setTab1SelectedIds([]);

  const toggleTab1Item = (id: string) =>
    setTab1SelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const tab1Results = (data?.results || []) as any[];
  const tab1AllSelected =
    tab1Results.length > 0 && tab1Results.every((a: any) => tab1SelectedIds.includes(a._id));
  const tab1SomeSelected =
    !tab1AllSelected && tab1Results.some((a: any) => tab1SelectedIds.includes(a._id));

  const toggleTab1All = (checked: boolean) => {
    if (checked) {
      setTab1SelectedIds(tab1Results.map((a: any) => a._id).filter(Boolean));
    } else {
      setTab1SelectedIds([]);
    }
  };

  // ── Agreement action dialog state ───────────────────────────────────────────
  const [agreementDialogTarget, setAgreementDialogTarget] = useState<string | null>(null);
  const closeAgreementDialog = () => setAgreementDialogTarget(null);
  const agreementDialogRow = tab1Results.find((r: any) => r._id === agreementDialogTarget) ?? null;

  // ── Bulk confirm dialog state ───────────────────────────────────────────────
  const [bulkMonitorableAction, setBulkMonitorableAction] = useState<"publish" | "unpublish" | null>(null);
  const [bulkRemoveConfirmOpen, setBulkRemoveConfirmOpen] = useState(false);

  // ── Mutations ───────────────────────────────────────────────────────────────
  const { mutate: toggleAgreementMonitoring, loading: updatingAgreement } = useMutation({
    mutationFn: ({ id, is_monitorable }: { id: string; is_monitorable: boolean }) =>
      axiosInstance.put(`/update-agreement-cgs-is-monitorable/${id}/${is_monitorable}`),
    onSuccess: () => fetchData(),
  });

  const { mutate: toggleCgsMonitoringPaidStatus, loading: updatingPaidStatus } = useMutation({
    mutationFn: ({ id, is_paid }: { id: string; is_paid: boolean }) =>
      axiosInstance.put(`/update-cgs-monitoring-application-payment-status/${id}/${is_paid}`),
    onSuccess: () => fetchData(),
  });

  const { mutate: toggleCgsMonitoringManyApplicationsPaidStatus, loading: updatingManyPaid } = useMutation({
    mutationFn: (applications: { id: string; is_paid: boolean }[]) =>
      axiosInstance.put(`/update-many-cgs-monitoring-application-payment-status`, { applications }),
    onSuccess: () => fetchData(),
  });

  const { mutate: bulkSetMonitorable, loading: startingBulk } = useMutation({
    mutationFn: ({ ids, is_monitorable }: { ids: string[]; is_monitorable: boolean }) =>
      axiosInstance.put("/update-agreement-cgs-is-monitorable-bulk", { ids, is_monitorable }),
    onSuccess: () => {
      clearTab1Selection();
      fetchData();
    },
  });

  const { mutate: bulkRemoveAgreements, loading: removingBulk } = useMutation({
    mutationFn: (ids: string[]) =>
      axiosInstance.delete("/applications/bulk", { data: { ids } }),
    onSuccess: () => {
      clearTab1Selection();
      fetchData();
    },
  });

  const [assignDialogOpen, setAssignDialogOpen] = React.useState(false);
  const [assignTargetApp, setAssignTargetApp] = React.useState<ApplicationPreview | null>(null);
  const [companyMembers, setCompanyMembers] = React.useState<{ _id: string; name: string }[]>([]);
  const [loadingMembers, setLoadingMembers] = React.useState(false);
  const [assigningMember, setAssigningMember] = React.useState(false);

  const openAssignDialog = (application: ApplicationPreview) => {
    setAssignTargetApp(application);
    setAssignDialogOpen(true);
    setLoadingMembers(true);
    axiosInstance
      .post("/fetch_users", { page: 0, limit: 100 })
      .then((res) => {
        const users = res.data.users || res.data;
        setCompanyMembers((Array.isArray(users) ? users : []).filter((u: any) =>
          u._id !== currentUser?._id && u.org_role === "member"
        ));
      })
      .catch(console.error)
      .finally(() => setLoadingMembers(false));
  };

  const handleAssignToMember = async (memberId: string | null) => {
    if (!assignTargetApp) return;
    setAssigningMember(true);
    try {
      await axiosInstance.put(`/monitoring/assign-to-member-cgs/${assignTargetApp._id}`, { assigned_to: memberId });
      setAssignDialogOpen(false);
      setAssignTargetApp(null);
      fetchData();
    } catch (err: any) {
      alert(err?.response?.data?.message || "შეცდომა");
    } finally {
      setAssigningMember(false);
    }
  };

  const canShowEditOrAdd = (tab: Type, application: ApplicationPreview): boolean =>
    evaluateEditRules(tab, application, { currentUser, canSubmitApp, canReviewApp, isAgency, isMonitoring, isCgs, assignments });

  // Clear TAB 1 selection when tab changes
  const isFirstRender = React.useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setFilter((prev) => ({ ...prev, type: tab, monitoring_approval_status: "", page: 1 }));
    clearTab1Selection();
  }, [tab]);

  let { options: GeneralInfo, generatedValues: GeneralInforValues } = useGeneralInfoContext();

  const columns: Column[] = [
    ...(tab === Type.agreement && (canManageAgreements || canDeleteAgreements) ? ([{ id: "bulk_select", label: "" }] as Column[]) : []),
    { id: "id", label: "" },
    ...(tab === Type.approved ? ([{ id: "is_paid", label: "ჩატარებული მონიტორინგის ანაზღაურების სტატუსი" }] as Column[]) : []),
    { id: "loan_id", label: "სესხის ID" },
    { id: "company_name", label: "ბენეფიციარის დასახელება" },
    { id: "company_identification_code", label: "ბენეფიციარის ს/კ" },
    { id: "approved_amount", label: "დამტკიცებული თანხა - ₾" },
    { id: "contract_signed_date", label: "დადასტურების თარიღი" },
    { id: "actual_municipality", label: "საქმიანობის განხორციელების ფაქტობრივი მისამართი - მუნიციპალიტეტი" },
    { id: "program_component", label: "პროგრამის კომპონენტი" },
    ...(tab !== Type.agreement ? ([{ id: "monitoring_assignment", label: "მონიტორინგის კომპანია" }] as Column[]) : []),
    ...(tab !== Type.agreement ? ([{ id: "created_by", label: "მონიტორინგის ინიციატორი" }] as Column[]) : []),
    ...(tab !== Type.agreement ? ([{ id: "assigned_to", label: "მონიტორინგის განმხორციელებელი პირი" }] as Column[]) : []),
    ...(tab !== Type.agreement ? ([{ id: "monitoring_approval_status", label: "სტატუსი" }] as Column[]) : []),
    ...(tab === Type.monitoring ? ([{ id: "monitoring_new_deadline", label: "მონიტორინგის განხორციელების ვადა" }] as Column[]) : []),
    ...(tab === Type.agreement ? ([{ id: "monitoring_deadline", label: "მონიტორინგის განხორციელების ვადა" }] as Column[]) : []),
    ...(tab === Type.approved ? ([{ id: "is_monitoring_deadline", label: "დამონიტორინგებული თუ არის ვადებში" }] as Column[]) : []),
  ];

  function redirectToDetailsOrCreate(url: string, callback: (a: string) => void) {
    if (callback) callback(url);
  }

  const bulkActions: BulkAction[] = [
    ...(canManageAgreements ? [{
      id: "publish",
      label: "გამოქვეყნება",
      variant: "success" as const,
      onClick: () => setBulkMonitorableAction("publish"),
      disabled: startingBulk || removingBulk,
    }] : []),
    ...(canManageAgreements ? [{
      id: "unpublish",
      label: "გაუქმება",
      variant: "warning" as const,
      onClick: () => setBulkMonitorableAction("unpublish"),
      disabled: startingBulk || removingBulk,
    }] : []),
    ...(canDeleteAgreements ? [{
      id: "remove",
      label: "წაშლა",
      variant: "danger" as const,
      onClick: () => setBulkRemoveConfirmOpen(true),
      disabled: startingBulk || removingBulk,
    }] : []),
  ];

  return (
    <>
      <Box sx={{ padding: 2 }}>
        <FilterGrid>
          <FilterTextField
            label="ბენეფიციარის დასახელება"
            value={filter.company_name}
            onChange={(e) => setFilterState({ company_name: e.target.value })}
          />
          <FilterTextField
            label="ბენეფიციარის ს/კ"
            value={filter.company_identification_code}
            onChange={(e) => setFilterState({ company_identification_code: e.target.value })}
          />
          <FilterSelect
            label="მუნიციპალიტეტი"
            labelId="municipality"
            value={filter.municipality}
            onChange={(e) => setFilterState({ municipality: e.target.value as string })}
          >
            <FilterMenuItem value=""><em>ყველა</em></FilterMenuItem>
            {GeneralInfo?.municipalities && Object.keys(GeneralInfo?.municipalities).map((key) => (
              <FilterMenuItem key={key} value={key}>{GeneralInfo?.municipalities[key].label}</FilterMenuItem>
            ))}
          </FilterSelect>
          {(tab === Type.monitoring || tab === Type.approved) && (
            <FilterSelect
              label="სტატუსი"
              labelId="status"
              value={filter?.monitoring_approval_status}
              onChange={(e) => setFilterState({ monitoring_approval_status: e.target.value as any })}
            >
              <FilterMenuItem value=""><em>ყველა</em></FilterMenuItem>
              {tabStatusMap[tab].map((item) => (
                <FilterMenuItem key={item} value={item}>
                  {monitoringStatus[item as keyof typeof monitoringStatus]}
                </FilterMenuItem>
              ))}
            </FilterSelect>
          )}
          {canSeeAllApps && isAgency && tab !== Type.agreement && (
            <AssignmentMultiSelect
              value={filter.monitoring_assignment || []}
              onChange={(val) => setFilterState({ monitoring_assignment: val })}
              assignments={assignments}
            />
          )}
        </FilterGrid>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
          {tab === Type.agreement && (
            <CustomInputFields
              key={1}
              name={"monitorable"}
              value={filter?.is_monitorable || false}
              onChange={() => setFilterState({ is_monitorable: filter.is_monitorable ? false : true })}
              type={"checkbox"}
              label={"მონიშნულები"}
            />
          )}
          {tab === Type.approved && (
            <CustomInputFields
              key={1}
              name={"is_paid"}
              value={filter?.is_paid || false}
              onChange={() => setFilterState({ is_paid: filter.is_paid ? false : true })}
              type={"checkbox"}
              label={"გადახდილები"}
            />
          )}
        </Box>
      </Box>
      <Divider />
      <CardContent sx={{ pb: tab === Type.agreement && tab1SelectedIds.length > 0 ? "80px" : undefined }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => {
                  if (column.id === "bulk_select") {
                    return (
                      <TableCell key="bulk_select" padding="checkbox" sx={{ pl: 1.5 }}>
                        <CustomInputFields
                          key="select-all"
                          name="selectAll"
                          indeterminate={tab1SomeSelected}
                          value={tab1AllSelected}
                          onChange={(checked) => toggleTab1All(checked)}
                          type="checkbox"
                          label=""
                          style={{ checkbox: { color: "#1976d2", checkedColor: "#1976d2", backgroundColor: "#fff", borderRadius: "2px" } }}
                        />
                      </TableCell>
                    );
                  }
                  if (column.id === "is_paid") {
                    return (
                      <TableCell key={column.id}>
                        {column.label}
                        {tab === Type.approved && isAgency
                          &&
                          <DialogWindow
                            key={"SelectedAll"}
                            openDialogWindow={isBulkAction}
                            txt={{
                              dialogTextTitle: "",
                              cancelButton: "გაუქმება",
                              triggerButton: "შეცვლა",
                              dialogTextBody: "დარწმუნებული ხართ, რომ სტატუსის გადახდის შეცვლა გსურთ?",
                            }}
                            callback={() => {
                              toggleCgsMonitoringManyApplicationsPaidStatus(selectedIds.map(({ _id, is_paid }) => ({ id: _id, is_paid })));
                            }}
                            setOpen={(open) => {
                              if (!open) {
                                setIsBulkAction(false);
                              }
                            }}
                          >
                            <CustomInputFields
                              key={"10000"}
                              name={"selectedsAll"}
                              indeterminate={someSelected}
                              value={allSelected}
                              onChange={(checked) => {
                                toggleSelectAll(
                                  checked,
                                  (data?.results && data.results.length > 0 && data?.results.map((app: any) => (
                                    { _id: app?._id, is_paid: app?.is_paid }
                                  ))) || []);
                              }}
                              type={"checkbox"}
                              label={""}
                            />
                          </DialogWindow >}
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell key={column.id}>{column.label}</TableCell>
                  )
                })}
                <TableCell key={1001}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data &&
                data?.results.map((application: any, index: number) => {
                  return (
                    <TableRow
                      sx={{
                        backgroundColor: getStatusColor(tab, application).backgroundColor,
                      }}
                      key={application._id}
                      onDoubleClick={() =>
                        redirectToDetailsOrCreate(
                          `${[Type.monitoring, Type.approved].includes(tab) ? "/monitoring-details-cgs/" : "/monitoring-agreement-details-cgs/"}${application._id
                          }`,
                          openInNewTab
                        )
                      }
                    >
                      {columns.map((column: Column) => {
                        let value;
                        const rowIndex = index + 1 + (filter.page - 1) * filter.amountOnPage;

                        if (column.id === "bulk_select") {
                          return (
                            <TableCell key="bulk_select" padding="checkbox" sx={{ pl: 1.5 }}>
                              <CustomInputFields
                                key={application._id + "_bulk"}
                                name={application._id + "_bulk"}
                                value={tab1SelectedIds.includes(application._id)}
                                onChange={() => toggleTab1Item(application._id)}
                                type="checkbox"
                                label=""
                                style={{ checkbox: { color: "#1976d2", checkedColor: "#1976d2", backgroundColor: "#fff", borderRadius: "2px" } }}
                              />
                            </TableCell>
                          );
                        } else if (column.id === "id") {
                          value = `${rowIndex} `;
                        } else if (column.id === "actual_municipality") {
                          if (GeneralInforValues && GeneralInforValues["municipalities"]) {
                            value = application[column.id]
                              ? GeneralInforValues["municipalities"][application[column.id] as keyof (typeof GeneralInforValues)["municipalities"]]
                                ?.municipality
                              : "მუნიციპალიტეტი არ არის მითითებული";
                          }
                        } else if (column.id === "monitoring_assignment") {
                          const ma = application.monitoring_assignment;
                          value = (typeof ma === "object" && ma?.organization?.name) ? ma.organization.name : "";
                        } else if (column.id === "created_by") {
                          value = application.created_by?.name ?? "";
                        } else if (column.id === "assigned_to") {
                          value = (application.assigned_to?.name || application.created_by?.name) ?? "";
                        } else if (column.id === "is_monitoring_deadline") {
                          const approvesHistory = (application?.monitoring_approval_history as IMonitoringApprovalHistory[]) || [];
                          const approvedDate = approvesHistory[approvesHistory.length - 1]?.approved_at || null;
                          const monitoringDeadline = application.monitoring_deadline;
                          const monitoringNewDeadline = application.monitoring_new_deadline;
                          const deadline = monitoringNewDeadline || monitoringDeadline;
                          value =
                            !approvedDate || !deadline
                              ? ""
                              : isMoreThanMonth(snapToGeorgiaEndOfDay(deadline).getTime(), new Date(approvedDate).getTime())
                                ? "არა"
                                : "კი";
                        } else if (column.id === "contract_signed_date") {
                          value = application[column.id] ? dateFormat(application[column.id], "dd/mm/yyyy") : "";
                        } else if (column.id === "monitoring_deadline") {
                          value = formatMonitoringDeadline(application[column.id]);
                        } else if (["monitoring_new_deadline"].includes(column.id) && tab === Type.monitoring) {
                          value = formatMonitoringDeadline(application.monitoring_new_deadline || application.monitoring_deadline);
                        } else if (column.id === "monitoring_approval_status") {
                          value = application[column.id as keyof ApplicationPreview]
                            ? monitoringStatus[application[column.id as keyof ApplicationPreview] as keyof typeof monitoringStatus]
                            : "fake status";
                        } else if (column.id === "is_paid") {
                          if (tab === Type.approved && isAgency) {
                            value = (
                              <DialogWindow
                                key={application._id + "paidStatus"}
                                openDialogWindow={isModalOpen}
                                txt={{
                                  dialogTextTitle: "",
                                  cancelButton: "გაუქმება",
                                  triggerButton: "შეცვლა",
                                  dialogTextBody: "დარწმუნებული ხართ, რომ გადახდის სტატუსის შეცვლა გსურთ?",
                                }}
                                callback={() => {
                                  if (singleSelected && singleSelected._id) {
                                    toggleCgsMonitoringPaidStatus({ id: singleSelected._id, is_paid: !singleSelected.is_paid });
                                  }
                                }}
                                setOpen={(open) => {
                                  if (!open) {
                                    setIsModalOpen(false);
                                  }
                                }}
                              >
                                <CustomInputFields
                                  key={application._id}
                                  name={application._id}
                                  value={application.is_paid}
                                  onChange={() => {
                                    toggleItem(application._id, application.is_paid || false);
                                  }}
                                  type={"checkbox"}
                                  label={""}
                                  style={{
                                    checkbox: {
                                      color: getStatusColor(tab, application).color ? "#fff" : undefined,
                                      checkedColor: getStatusColor(tab, application).color ? "#fff" : undefined,
                                    },
                                  }}
                                />
                              </DialogWindow >
                            )
                          } else {
                            value = application[column.id as keyof ApplicationPreview]
                              ? "გადახდილია"
                              : "არ არის გადახდილი";
                          }
                        } else {
                          value = application[column.id as keyof ApplicationPreview];
                        }
                        return (
                          <TableCell
                            sx={{
                              color: getStatusColor(tab, application).color ? "#fff" : "inherit",
                              fontWeight: getStatusColor(tab, application).color ? "bold" : "normal",
                            }}
                            key={column.id}
                          >
                            {value || ""}
                          </TableCell>
                        );
                      })}
                      <TableCell key={`${application._id} - ${index}`} align="left">
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            width: "100px",
                          }}
                        >
                          {!application?.monitoring_approval && canShowEditOrAdd(tab, application) ? (
                            <CustomTooltip title={tab === Type.agreement ? "მონიტორინგი" : "რედაქტირება"} placement="bottom-end" arrow>
                              <IconButton
                                aria-label="edit"
                                size="small"
                                onClick={() =>
                                  tab === Type.monitoring
                                    ? redirectToDetailsOrCreate(`/monitoring-update-cgs-form/${application._id}`, openInNewTab)
                                    : redirectToDetailsOrCreate(`/monitoring-start-cgs-form/${application._id}`, openInNewTab)
                                }
                              >
                                {tab === Type.monitoring ? (
                                  <EditOutlinedIcon
                                    sx={{
                                      strokeWidth: 1,
                                      stroke: getStatusColor(tab, application).color ? "#fff" : "#56B9E9",
                                      fontSize: "1rem",
                                      color: getStatusColor(tab, application).color ? "#fff" : "#56B9E9",
                                    }}
                                  />
                                ) : (
                                  <>
                                    {(isMonitoring && isCgs) && application.is_monitorable ? (
                                      <AddCircleOutlinedIcon
                                        sx={{
                                          strokeWidth: 1,
                                          fontSize: "1rem",
                                          color: getStatusColor(tab, application).color ? "#fff" : "#56B9E9",
                                        }}
                                      />
                                    ) : null}
                                  </>
                                )}
                              </IconButton>
                            </CustomTooltip>
                          ) : null}
                          {tab === Type.monitoring && isHead && isMonitoring && application.monitoring_assignment ? (
                            <CustomTooltip title="გადამისამართება" placement="bottom-end" arrow>
                              <IconButton
                                aria-label="assign"
                                size="small"
                                onClick={() => openAssignDialog(application)}
                              >
                                <PersonAddAlt1Icon
                                  sx={{
                                    fontSize: "1rem",
                                    color: getStatusColor(tab, application).color ? "#fff" : "#56B9E9",
                                  }}
                                />
                              </IconButton>
                            </CustomTooltip>
                          ) : null}
                          {/* {tab === Type.agreement && canManageAgreements ? (
                            <CustomInputFields
                              key={application._id}
                              name={application._id}
                              value={application.is_monitorable}
                              onChange={() => setAgreementDialogTarget(application._id)}
                              type={"checkbox"}
                              label={""}
                              style={{
                                checkbox: {
                                  color: getStatusColor(tab, application).color ? "#fff" : undefined,
                                  checkedColor: getStatusColor(tab, application).color ? "#fff" : undefined,
                                },
                              }}
                            />
                          ) : null} */}
                          {[Type.monitoring, Type.approved].includes(tab) && application.monitoring_approval && (
                            <CustomTooltip title="დადასტურებული" placement="bottom-end" arrow>
                              <IconButton aria-label="detail" size="small" sx={{ fontSize: "1em" }}>
                                <DownloadDoneOutlinedIcon
                                  sx={{
                                    strokeWidth: 1,
                                    color: getStatusColor(tab, application).color ? "#fff" : "#00695f",
                                    fontSize: "1.1rem",
                                  }}
                                />
                              </IconButton>
                            </CustomTooltip>
                          )}
                          <CustomTooltip
                            title={
                              tab === Type.agreement
                                ? "დასამონიტორინგებელი პროექტის დეტალები"
                                : `${tab === Type.monitoring ? "დამონიტორინგებული" : "დადასტურებული"} პროექტის დეტალები`
                            }
                            placement="bottom-end"
                            arrow
                          >
                            <IconButton
                              aria-label="detail"
                              size="small"
                              sx={{ fontSize: "1em" }}
                              onClick={() =>
                                redirectToDetailsOrCreate(
                                  `${[Type.monitoring, Type.approved].includes(tab) ? "/monitoring-details-cgs/" : "/monitoring-agreement-details-cgs/"}${application._id
                                  }`,
                                  openInNewTab
                                )
                              }
                            >
                              <InfoOutlinedIcon
                                fontSize="inherit"
                                sx={{
                                  strokeWidth: 1,
                                  stroke: getStatusColor(tab, application).color ? "#fff" : "#56B9E9",
                                  color: getStatusColor(tab, application).color ? "#fff" : "#56B9E9",
                                  fontSize: "1rem",
                                }}
                              />
                            </IconButton>
                          </CustomTooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {load && <TableSkeleton amount={5} columns={columns} />}
            </TableBody>
          </Table>
        </TableContainer>
        <Pagination
          count={data?.pages || 1}
          page={filter.page || 1}
          onChange={(event, value) => setFilter({ ...filter, page: value })}
          rowsPerPage={filter.amountOnPage || 15}
          onRowsPerPageChange={(event) => setFilter({ ...filter, amountOnPage: event.target.value as number, page: 1 })}
        />
      </CardContent>

      {/* ── Bulk actions bar (TAB 1 only) ─────────────────────────────────── */}
      {tab === Type.agreement && (
        <BulkActionsBar
          selectedCount={tab1SelectedIds.length}
          actions={bulkActions}
          onClear={clearTab1Selection}
          isLoading={startingBulk || removingBulk}
        />
      )}

      {/* ── Agreement action dialog (checkbox → dialog) ───────────────────── */}
      <Dialog open={agreementDialogTarget !== null} onClose={closeAgreementDialog} maxWidth="xs" fullWidth>
        <DialogTitle>მოქმედების არჩევა</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {agreementDialogRow?.company_name || ""}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="success"
            variant="contained"
            disabled={updatingAgreement}
            onClick={() => {
              if (agreementDialogTarget) {
                toggleAgreementMonitoring({ id: agreementDialogTarget, is_monitorable: true });
              }
              closeAgreementDialog();
            }}
          >
            დამატება
          </Button>
          {canManageAgreements && (
            <Button
              color="error"
              variant="contained"
              disabled={updatingAgreement}
              onClick={() => {
                if (agreementDialogTarget) {
                  toggleAgreementMonitoring({ id: agreementDialogTarget, is_monitorable: false });
                }
                closeAgreementDialog();
              }}
            >
              წაშლა
            </Button>
          )}
          <Button onClick={closeAgreementDialog}>გაუქმება</Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk publish / unpublish confirm dialog ──────────────────────── */}
      <Dialog open={bulkMonitorableAction !== null} onClose={() => setBulkMonitorableAction(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{bulkMonitorableAction === "publish" ? "გამოქვეყნება" : "გაუქმება"}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            დარწმუნებული ხართ, რომ შერჩეული {tab1SelectedIds.length} ხელშეკრულება{" "}
            {bulkMonitorableAction === "publish" ? "გამოქვეყნდეს (is_monitorable = true)" : "გაუქმდეს (is_monitorable = false)"}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkMonitorableAction(null)} disabled={startingBulk}>
            გაუქმება
          </Button>
          <Button
            color={bulkMonitorableAction === "publish" ? "success" : "warning"}
            variant="contained"
            disabled={startingBulk}
            onClick={() => {
              bulkSetMonitorable({ ids: tab1SelectedIds, is_monitorable: bulkMonitorableAction === "publish" });
              setBulkMonitorableAction(null);
            }}
          >
            {bulkMonitorableAction === "publish" ? "გამოქვეყნება" : "გაუქმება"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk remove confirm dialog ────────────────────────────────────── */}
      <Dialog open={bulkRemoveConfirmOpen} onClose={() => setBulkRemoveConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>წაშლა მონაცემთა ბაზიდან</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            დარწმუნებული ხართ, რომ შერჩეული {tab1SelectedIds.length} ხელშეკრულება წაიშალოს მონაცემთა ბაზიდან?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkRemoveConfirmOpen(false)} disabled={removingBulk}>
            გაუქმება
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={removingBulk}
            onClick={() => {
              bulkRemoveAgreements(tab1SelectedIds);
              setBulkRemoveConfirmOpen(false);
            }}
          >
            წაშლა
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Assign to member dialog ───────────────────────────────────────── */}
      <Dialog open={assignDialogOpen} onClose={() => { setAssignDialogOpen(false); setAssignTargetApp(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>გადამისამართება</DialogTitle>
        <DialogContent dividers>
          {loadingMembers ? (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
          ) : companyMembers.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
              წევრები ვერ მოიძებნა
            </Typography>
          ) : (
            <List dense disablePadding>
              {companyMembers.map((member) => (
                <ListItemButton key={member._id} disabled={assigningMember} onClick={() => handleAssignToMember(member._id)}>
                  <ListItemText primary={member.name} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          {(assignTargetApp as any)?.assigned_to && (
            <Button color="warning" disabled={assigningMember} onClick={() => handleAssignToMember(null)} sx={{ mr: "auto" }}>
              გადამისამართების გაუქმება
            </Button>
          )}
          <Button onClick={() => { setAssignDialogOpen(false); setAssignTargetApp(null); }}>დახურვა</Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default MonitoringDashboard;
